import * as React from "react";
import { getQueryParameterMap } from "../utils/common-utils";
import { resolveHighlightParams, HighlightParams } from "../utils/highlight-rect-utils";
import { useHistoryChangeSubscription } from "./useRouteType";

/**
 * Hook that tracks highlight-related URL params (`highlight`, `container`,
 * `highlightContext`) as React state, syncing on SPA navigation.
 *
 * Uses the same triple-listen pattern as useRouteType: monkey-patched
 * pushState/replaceState (via subscribe) + hashchange + popstate. HashRouter
 * uses pushState/replaceState internally and they don't fire native events —
 * hashchange alone misses in-app navigation.
 *
 * Owns the three param `useState`s, the initial `useMemo`, the prevParams
 * reset logic, and the subscription lifecycle — keeping HighlightHandler.tsx
 * focused on rendering and observation.
 *
 * @param onParamsChanged Called when highlight params actually change (not on
 *   initial mount or StrictMode re-invoke). Consumers use this to clear stale
 *   overlay/range state. The callback identity is not tracked as a dependency —
 *   pass a stable reference (e.g. useCallback) if the consumer needs to change
 *   it at runtime.
 */
export function useHighlightParams(onParamsChanged?: () => void): HighlightParams {
    const initialParams = React.useMemo(() => resolveHighlightParams(getQueryParameterMap()), []);
    const [highlightParam, setHighlightParam] = React.useState<string | null>(initialParams.highlight);
    const [containerId, setContainerId] = React.useState<string | null>(initialParams.container);
    const [highlightContext, setHighlightContext] = React.useState<string | null>(initialParams.highlightContext);

    // Stable ref for the callback so the reset effect doesn't re-run when
    // the consumer passes a new closure identity.
    const onParamsChangedRef = React.useRef(onParamsChanged);
    onParamsChangedRef.current = onParamsChanged;

    // Sync URL params to state on SPA navigation via shared hook.
    const handleRouteChange = React.useCallback(() => {
        const resolved = resolveHighlightParams(getQueryParameterMap());
        setHighlightParam(resolved.highlight);
        setContainerId(resolved.container);
        setHighlightContext(resolved.highlightContext);
    }, []);

    useHistoryChangeSubscription(handleRouteChange);

    // Catch any navigation that happened between the initial state seeding
    // (the useMemo above) and the subscription being wired up.
    React.useEffect(() => {
        const current = resolveHighlightParams(getQueryParameterMap());
        if (
            current.highlight !== initialParams.highlight ||
            current.container !== initialParams.container ||
            current.highlightContext !== initialParams.highlightContext
        ) {
            handleRouteChange();
        }
    }, [initialParams, handleRouteChange]);

    // Reset stale state whenever highlight params actually change. Uses a
    // prevParams ref instead of an isInitialMount flag so it's idempotent under
    // React 18 StrictMode (which double-invokes effects on mount — a boolean
    // ref gets corrupted, but a value comparison is stable).
    const prevParams = React.useRef<HighlightParams>({ highlight: highlightParam, container: containerId, highlightContext });
    React.useEffect(() => {
        const p = prevParams.current;
        if (p.highlight === highlightParam && p.container === containerId && p.highlightContext === highlightContext) {
            return; // no real change (also covers StrictMode re-invoke on mount)
        }
        prevParams.current = { highlight: highlightParam, container: containerId, highlightContext };
        onParamsChangedRef.current?.();
    }, [highlightParam, containerId, highlightContext]);

    return { highlight: highlightParam, container: containerId, highlightContext };
}
